import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, Checkbox, Grid, GridItem, Text, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { sortStudentsAlphabetically } from "./utils";
import { colours } from "@/utils/themeUtils";
import { ChakraFAIcon } from "../ui/Icon";

export interface SelectableStudent {
	/**
	 * How the checkbox is identified. Generally should be the student's ID.
	 */
	value: string;
	/**
	 * Checkbox label. Generally should be the student's name.
	 */
	label: string;
	isStudentClaimed: boolean;
	disabled?: boolean;
	disabledReason?: string;
}

interface Props {
	students: SelectableStudent[];
	handleSelectionChange: (selectedStudents: string[]) => void;
	/**
	 * All students are pre-selected by default. If this prop is provided, only the given students will be pre-selected.
	 */
	preselectedStudentValues?: string[];
}

export function StudentCheckboxSelector(props: Props): JSX.Element {
	const { t } = useTranslation();
	function getStudentsToAutoSelect(): string[] {
		// Don't preselect students that are disabled, even if they are in the preselectedStudentValues array.
		if (props.preselectedStudentValues && props.preselectedStudentValues?.length >= 1) {
			// Create a Set of disabled student values for O(1) lookup performance
			const disabledStudentValues = new Set(props.students.filter((student) => student.disabled).map((student) => student.value));
			return props.preselectedStudentValues.filter((value) => !disabledStudentValues.has(value));
		}
		return props.students.filter((student) => student.isStudentClaimed && !student.disabled).map((student) => student.value);
	}

	const [selectedStudents, setSelectedStudents] = useState<string[]>(getStudentsToAutoSelect());

	// Whenever selectedStudents changes (including mount), call handleSelectionChange so parent can use the updated selection
	useEffect(() => {
		props.handleSelectionChange(selectedStudents);
	}, [props, selectedStudents]);

	function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>): void {
		const studentId = event.target.id;

		// Don't allow changes for disabled students
		const student = props.students.find((s) => s.value === studentId);
		if (student?.disabled) {
			return;
		}

		const updatedSelection = selectedStudents.includes(studentId)
			? selectedStudents.filter((id) => id !== studentId)
			: [...selectedStudents, studentId];

		setSelectedStudents(updatedSelection);
	}

	function handleSelectAllChange(event: ChangeEvent<HTMLInputElement>): void {
		if (event.target.checked) {
			const allEnabledStudents = props.students
				.filter((student) => student.isStudentClaimed && !student.disabled)
				.map((student) => student.value);
			setSelectedStudents(allEnabledStudents);
		} else {
			setSelectedStudents([]);
		}
	}

	const COLUMN_COUNT = 2;

	function getRows(items: SelectableStudent[]): SelectableStudent[][] {
		const sortedItems = sortStudentsAlphabetically(items);

		const rows: SelectableStudent[][] = [];
		for (let i = 0; i < sortedItems.length; i += COLUMN_COUNT) {
			rows.push(sortedItems.slice(i, i + COLUMN_COUNT));
		}
		return rows;
	}

	const rows = getRows(props.students);

	// Determine the state of the "All Students" checkbox
	const isAllSelected =
		selectedStudents.length === props.students.filter((student) => student.isStudentClaimed && !student.disabled).length;
	const isIndeterminate = !isAllSelected && selectedStudents.length > 0;
	const isAllDisabled = props.students.every((student) => student.disabled);

	return (
		<Box>
			<Checkbox
				isIndeterminate={isIndeterminate}
				isChecked={!isAllDisabled && isAllSelected}
				onChange={handleSelectAllChange}
				spacing={4}
				fontWeight={500}
				pb={5}
				data-testid="select-all-students-checkbox"
				isDisabled={isAllDisabled}
				sx={{
					"[data-indeterminate]": {
						background: "var(--primary-color)",
						borderColor: "var(--primary-color)",
					},
				}}
			>
				{t("students.selectAll")}
			</Checkbox>
			<Box border={"1px solid #EBECF0"} rounded={"md"}>
				{rows.map((gridrow, index) => (
					<Box key={index} _odd={{ bg: `${colours.neutrals[50]}99` }}>
						<Grid templateColumns={`repeat(${COLUMN_COUNT}, 1fr)`}>
							{gridrow.map((student, index) => (
								<GridItem key={index} p={3}>
									<Checkbox
										isChecked={student.isStudentClaimed && !student.disabled && selectedStudents.includes(student.value)}
										id={student.value}
										onChange={handleCheckboxChange}
										spacing={3}
										maxWidth="200px"
										isDisabled={!student.isStudentClaimed || student.disabled}
										title={student.disabledReason}
									>
										{student.label}
									</Checkbox>
									{!student.isStudentClaimed && (
										<Tooltip label={t("students.managedByOtherClass")}>
											<ChakraFAIcon icon="fa-question-circle" variant="solid" color={"neutrals.500"} mx={2} />
										</Tooltip>
									)}
									{student.isStudentClaimed && student.disabled && student.disabledReason && (
										<Text mt={2} color={"neutrals.500"} fontSize={"sm"}>
											{student.disabledReason}
										</Text>
									)}
								</GridItem>
							))}
						</Grid>
					</Box>
				))}
			</Box>
		</Box>
	);
}
